import React, { FC, useState } from 'react';
import { HStack, Stack } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import TrackView from 'features/Analytics/TrackView';
import * as Layout from 'layouts/ContentLayout';

import Heading from '../../../../components/_core/Heading';
import Text from '../../../../components/_core/Text';
import { FilterPill } from '../../../Dashboard/pages/components/FilterGroup';
import EstimationHistory from './components/EstimationHistory';
import EstimationOverview from './components/EstimationOverview';
import { useTaxForecastData } from './hooks/useTaxForecastData';
import { CompanyType } from './hooks/useTrackMachine';
import { useUserProfileData } from './hooks/useUserProfileData';
import locales from './locales';
import { EstimationTab } from './types';

const Estimation: FC = () => {
  const { email, loading: userDataLoading, uid } = useUserProfileData();
  const [currentTab, setCurrentTab] = useState<EstimationTab>(
    EstimationTab.estimation,
  );

  const { loading, taxForecastPeriods, taxForecastProfile } =
    useTaxForecastData();

  const hasCustomizedEstimation =
    taxForecastProfile?.hasCustomizedEstimation &&
    Boolean(taxForecastPeriods?.length);

  return (
    <Layout.Root>
      <Layout.Body>
        <TrackView
          eventProperties={{
            step: 'pre-form',
            type: CompanyType.MICRO,
          }}
          pageName="UrssafEstimator"
        />
        <Stack marginBottom="space-40" spacing="space-8">
          <Stack spacing="space-8">
            <HStack alignItems="center" justifyContent="space-between">
              <Heading size="lg">{locales.title}</Heading>
              <Button
                as="a"
                href={
                  locales.formatString(locales.feedback.feedbackLink, {
                    email: email ?? '',
                    uid: uid ?? '',
                  }) as string
                }
                icon="link"
                isLoading={userDataLoading}
                rel="noopener noreferrer"
                target="_blank"
                variant="secondary"
              >
                {locales.feedback.feedbackCTA}
              </Button>
            </HStack>
            <Text size="lg">
              {locales.subtitle}
              {hasCustomizedEstimation ? (
                <>
                  &nbsp;
                  <Button
                    as="a"
                    href={locales.faqURL}
                    rel="noopener noreferrer"
                    target="_blank"
                    variant="inline-secondary"
                  >
                    {locales.readTheManual}
                  </Button>
                </>
              ) : null}
            </Text>
          </Stack>

          <HStack marginY="space-32" spacing="space-8">
            <FilterPill
              icon="sparkles"
              isActive={currentTab === EstimationTab.estimation}
              onClick={() => setCurrentTab(EstimationTab.estimation)}
            >
              {locales.tabs.simulator}
            </FilterPill>
            <FilterPill
              icon="receipt"
              isActive={currentTab === EstimationTab.history}
              onClick={() => setCurrentTab(EstimationTab.history)}
            >
              {locales.tabs.history}
            </FilterPill>
          </HStack>

          {currentTab === EstimationTab.estimation ? (
            <EstimationOverview
              // @ts-expect-error these values depend on loading
              hasCustomizedEstimation={hasCustomizedEstimation}
              // @ts-expect-error these values depend on loading
              isSubjectToVat={taxForecastProfile?.isSubjectToVat}
              loading={loading}
              taxForecastPeriods={taxForecastPeriods}
            />
          ) : null}

          {currentTab === EstimationTab.history ? <EstimationHistory /> : null}
        </Stack>
      </Layout.Body>
    </Layout.Root>
  );
};

export default Estimation;
